import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IAIModelFile,
	IAIModelFileDetails,
	IUploadAIModelFileThunkArgs,
	IGetAIModelFilesThunkArgs,
	IUpdateAIModelFileThunkArgs,
	IDeleteAIModelFileThunkArgs,
} from 'src/features/ai_model_file/domain/types';
import { AIModelFileApi } from 'src/features/ai_model_file/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const AI_MODEL_FILE_LOADING_KEY = 'ai_model_file';

export const uploadAIModelFile = createAsyncThunk<
	IAIModelFile,
	IUploadAIModelFileThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model_file/uploadAIModelFile',
	async (
		{ aiModelId, siteSlug, request, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelFileApi.uploadAIModelFile(
				aiModelId,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to upload AI model file',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAIModelFiles = createAsyncThunk<
	IPaginatedResponse<IAIModelFileDetails>,
	IGetAIModelFilesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model_file/getAIModelFiles',
	async (
		{ aiModelId, siteSlug, params, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelFileApi.getAIModelFiles(
				aiModelId,
				params,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch AI model files',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const updateAIModelFile = createAsyncThunk<
	IAIModelFileDetails,
	IUpdateAIModelFileThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model_file/updateAIModelFile',
	async (
		{ fileId, siteSlug, request, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelFileApi.updateAIModelFile(
				fileId,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to update AI model file',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const deleteAIModelFile = createAsyncThunk<
	void,
	IDeleteAIModelFileThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model_file/deleteAIModelFile',
	async (
		{ fileId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: true,
				}),
			);
			await AIModelFileApi.deleteAIModelFile(fileId, siteSlug);

			onSuccess?.();
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to delete AI model file',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_FILE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
